import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { cloneDeep } from "lodash";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import { Icons, Box, Card, Typography } from "@curves/ui-base";
import useToggleDialog from "@util/hook/useToggleDialog";
import useShowPDF from "@util/hook/useShowPDF";
import { getErrorModuleStatus, mergeDeep, toHash, isEmpty, isEqual } from "@util/utils";
import { MembershipProvider } from "@icoach/documents/membership/MembershipContext";
import MembershipCreateNewMemberBox from "@icoach/documents/membership/create/MembershipCreateNewMemberBox";
import MembershipCreateContentSurvey from "@icoach/documents/membership/create/MembershipCreateContentSurvey";
import MembershipCreateContentBasic from "@icoach/documents/membership/create/MembershipCreateContentBasic";
import MembershipCreateContentFee from "@icoach/documents/membership/create/MembershipCreateContentFee";
import MembershipCreateContentPayment from "@icoach/documents/membership/create/MembershipCreateContentPayment";
import MembershipCreateContentPlan, { handlePlansData } from "@icoach/documents/membership/create/MembershipCreateContentPlan";
import { buildRangeFeeParams, mapRangeFeeResponse } from "@icoach/documents/membership/create/membershipFormRules";
import { dialogTypeMap } from "@icoach/documents/membership/staticData";
import { MembershipBillingRecordDialog, MembershipRecordDialog } from "@icoach/documents/membership/dialog";
import { DocumentStepper } from "@icoach/documents/components";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import useDocMembershipApi from "@apis/useDocMembershipApi";
import { PEOPLE_TYPE } from "@icoach/constants/";
import { mapStepsWithErrorState, mergeMembershipFormData } from "@icoach/documents/tools";
import useValidationErrors from "@util/hook/useValidationErrors";
import FocusModeToggle from "@icoach/components/FocusModeToggle";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import useMembershipStepper from "@icoach/documents/membership/hooks/useMembershipStepper";
import { resolvePrivacyItemIndex } from "@icoach/documents/membership/create/versions";
import useMessageDialog from "@util/hook/useMessageDialog";

// 入會申請書步驟內容
const tabsData = [
	{
		id: "contractMain",
		label: "入會方案",
		content: <MembershipCreateContentPlan />,
		keyMapping: [
			"faDate",
			"employeeID",
			"applyDate",
			"membershipType",
			"recoveryType",
			"sourceTypeSelector",
			"memberType",
			"paymentType",
			"membershipTerm",
			"startDate",
			"endDate",
			"plans",
			"voucherCode",
			"contractFee.monthlyFee",
			"notes",
		],
	},
	{
		id: "contractSurvey",
		label: "會員須知",
		content: <MembershipCreateContentSurvey />,
		keyMapping: ["items", "contractSurvey.isConfirm", "contractSurvey.signImg"],
	},
	{
		id: "contractFee",
		label: "初次費用",
		content: <MembershipCreateContentFee />,
		keyMapping: ["joinFee", "firstMonthFee", "nextMonthFee", "cardPaymentAmount", "cashPaymentAmount", "contractFee.isConfirm", "contractFee.signImg", "isImmediatelyIssuing"],
	},
	{
		id: "contractBasic",
		label: "基本資料",
		content: <MembershipCreateContentBasic />,
		keyMapping: [
			"memberName",
			"nickName",
			"identifierID",
			"birthday",
			"contractBasic.signImg",
			"mail",
			"cellPhone",
			"tel",
			"companyTel",
			"postCode",
			"address",
			"contactName",
			"contactPhone",
			"contactship",
			"memberCardNo",
		],
	},
	{
		id: "contractPayment",
		label: "扣款授權",
		content: <MembershipCreateContentPayment />,
		keyMapping: [
			"debitStartDate",
			"debitEndDate",
			"debitFirstDate",
			"debitDayOfMonth",
			"contractPayment.signImg",
			"creditCardBankName",
			"creditCardType",
			"creditCardNo",
			"cardExpiryDate",
			"cardName",
			"cardRelation",
			"bankCode",
			"bankName",
			"bankAccount",
			"bankAccountName",
			"bankAccountID",
			"invoiceCarrierType",
			"invoiceCarrierNo",
			"invoiceIssuingType",
			"invoiceTitle",
			"invoiceTaxID",
			"invoiceLoveCode",
		],
	},
];

const SURVEY_STEP_INDEX = tabsData.findIndex(item => item.id === "contractSurvey");

const WorkspaceBox = props => {
	const { handleOpenDialog } = props;
	return (
		<Box className="workspace-box">
			<SpeedDial ariaLabel="SpeedDial openIcon example" icon={<SpeedDialIcon openIcon={<Icons.Edit />} />}>
				<SpeedDialAction icon={<Icons.UserCircle />} tooltipTitle={"會籍紀錄"} onClick={() => handleOpenDialog(dialogTypeMap.membershipRecord)} />
				<SpeedDialAction icon={<Icons.Dollar htmlColor="#a45bc8" />} tooltipTitle={"收費明細"} onClick={() => handleOpenDialog(dialogTypeMap.membershipBillingRecord)} />
			</SpeedDial>
		</Box>
	);
};

/**
 * isReadOnly 是否唯讀
 * onReturnHistory 返回總覽頁面
 * isApplySuccess 是否為申請成功，用來轉倒回會員選項
 */
const MembershipCreateContent = props => {
	const { isReadOnly = false, onReturnHistory } = props;
	const { referenceID: referenceIDProps, contractID: contractIDProps, peopleType: peopleTypeProps } = useParams();
	const { getDocMembershipApi, postDocMembershipApi, postDocMembershipTemporaryApi, postDocMembershipRangeFeeApi } = useDocMembershipApi();
	const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
	const showPDF = useShowPDF();
	const history = useHistory();
	// 判斷是否阻擋重整
	const [tabs, setTabs] = useState(tabsData);
	const [isMounted, setMounted] = useState(false);
	const [sourceData, setSourceData] = useState({});
	const [options, setOptions] = useState({});
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const setMessageDialog = useMessageDialog();
	const [newMemberAsset, setNewMemberAsset] = useState({});
	const { currentStep, contentRef, goToStep, getCurrentStepResult } = useMembershipStepper({
		stepCount: tabs.length,
		surveyStepIndex: SURVEY_STEP_INDEX,
		isReadOnly,
		readSurveyState: stepResult => {
			const mergedData = mergeMembershipFormData(sourceData, stepResult);
			return {
				contractSurvey: mergedData?.contractSurvey,
				// 105 以前沒有個資卡片，null 代表只檢查總同意與簽名。
				privacyItemIndex: resolvePrivacyItemIndex(mergedData?.contractVersion),
				isNoticeReady: true,
			};
		},
		onCollect: result => setSourceData(prev => mergeMembershipFormData(prev, result)),
		onBlocked: () =>
			setMessageDialog({
				open: true,
				title: "提醒",
				msg: "需讀取會員須知及個資說明方可填寫會員資訊",
				buttonText: "我知道了",
				buttonColor: "secondary",
			}),
	});
	const apiSendingRef = useRef(false);
	const {
		membershipID,
		contractID,
		memberID,
		contractVersionText,
		targetType,
		isCompleteContract, // 是否為正式文件
	} = sourceData;

	const focusModeRef = useRef(null);

	const checkStepsForErrors = useCallback(mapStepsWithErrorState, [JSON.stringify(validationErrors)]);

	const updateRootField = useCallback((key, value) => {
		setSourceData(prev => ({ ...prev, [key]: value }));
	}, []);

	useInactivityTimerToggle();

	const getParams = () => {
		const stepResult = getCurrentStepResult();
		return mergeMembershipFormData(sourceData, stepResult);
	};

	// 確認送出入會申請入 按鈕
	const handleSubmitCreate = () => {
		if (apiSendingRef.current) return false;
		clearValidationErrors();
		const params = getParams();
		doPostDocMembershipApi(contractIDProps, params);
	};

	// 暫存入會申請書 按鈕
	const handleSubmitTemporary = () => {
		if (apiSendingRef.current) return false;
		const params = getParams();
		apiSendingRef.current = true;
		doDocMembershipTemporaryApi(params);
	};

	const handleChangeRangeFee = params => {
		if (apiSendingRef.current) return false;
		if (!isEmpty(params)) {
			apiSendingRef.current = true;
			params["tabIndex"] = currentStep + 1;
			doDocMembershipRangeFeeApi(params);
		}
	};

	// 取得入會申請書資料
	const doGetDocMembershipApi = async (contractID, params) => {
		const res = await getDocMembershipApi(contractID, params);
		if (res) {
			const { resource, ...others } = res;
			setSourceData(others);
			setOptions(resource);
			setMounted(true);
		}
	};

	// 新增/重填入會申請書
	const doPostDocMembershipApi = async (contractID = 0, params) => {
		apiSendingRef.current = true;
		const resp = await postDocMembershipApi(contractID, params);
		const { isError = false, result, completedPath, htmlPath, memberID: mID, contractID: cID } = resp || {};

		if (isError) {
			// 0002 錯誤
			if (!isEmpty(result) && result.length > 0) {
				updateValidationErrors(result);
				setTabs(checkStepsForErrors(tabsData, toHash(result, "key", "message")));
			}
		} else if (isEmpty(resp)) {
			// 失敗
			setTabs(tabsData);
		} else {
			// 成功
			const routerPath = CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.GET_READONLY_PATH(2, mID, cID);

			showPDF({
				open: true,
				pdfUrl: completedPath,
				htmlUrl: htmlPath || "",
				fileName: "入會申請書",
			});

			// 清除錯誤模組和標籤，隱藏提示
			setTabs(tabsData);
			setNewMemberAsset({ memberID: mID });

			window.setTimeout(() => {
				history.replace(routerPath);
			}, 5);
		}
		apiSendingRef.current = false;
	};

	// 暫存入會申請書
	const doDocMembershipTemporaryApi = async params => {
		const resp = await postDocMembershipTemporaryApi(params);
		if (resp) {
			window.setTimeout(() => {
				onReturnHistory && onReturnHistory();
			}, 5);
		} else {
			apiSendingRef.current = false;
		}
	};

	// 依入會方案取得合約日期、會籍日期、初次費用
	const doDocMembershipRangeFeeApi = async params => {
		const _params = buildRangeFeeParams({ sourceData, params, handlePlansData });
		setSourceData(prev => ({ ...prev, contractMain: { ...prev.contractMain, voucherCode: _params.voucherCode } }));
		const resp = await postDocMembershipRangeFeeApi(_params);
		if (resp) {
			const result = mapRangeFeeResponse(resp, { membershipType: _params.membershipType });
			setSourceData(prev => {
				let prevData = cloneDeep(prev);
				return mergeDeep(prevData, result);
			});
		}
		apiSendingRef.current = false;
	};

	useEffect(
		() => {
			doGetDocMembershipApi(contractIDProps, { type: peopleTypeProps, id: referenceIDProps });
			return () => {
				setMounted(false);
			};
		},
		// eslint-disable-next-line
		[],
	);

	useEffect(
		() => {
			// 重刷頁面才會用 REPLACE
			if (isEqual(history.action, "REPLACE")) {
				doGetDocMembershipApi(contractIDProps, { type: peopleTypeProps, id: referenceIDProps });
			}
		},
		// eslint-disable-next-line
		[history.location],
	);

	if (!isMounted) return null;

	return (
		<MembershipProvider
			value={{
				data: sourceData,
				options,
				errorModule: validationErrors,
				isReadOnly,
				handleCloseDialog,
				getErrorModuleStatus,
				handleChangeRangeFee,
				updateRootField,
			}}
		>
			<FocusModeToggle ref={focusModeRef} showToggleFocusModeButton={!isReadOnly} defaultFocusMode={!isReadOnly}>
				<Card>
					<Card.CardContent>
						<DocumentStepper
							steps={tabs}
							currentStep={currentStep}
							contentRef={contentRef}
							onStepChange={goToStep}
							onTemporary={() => focusModeRef.current.checkAndExitFocusMode(handleSubmitTemporary)}
							onSubmit={() => focusModeRef.current.checkAndExitFocusMode(handleSubmitCreate)}
							onCancel={() => history.goBack()}
							onReturnHistory={() => onReturnHistory(newMemberAsset.memberID)}
							isTemporary={!isReadOnly && !isEqual(targetType, PEOPLE_TYPE.ONLYJOIN) && !isCompleteContract}
							isSubmit={!isReadOnly}
							isCancel={!isReadOnly}
							isBack={isReadOnly}
							ButtonEls={<MembershipCreateNewMemberBox memberID={newMemberAsset.memberID || null} />}
						/>
						<Typography variant="body2" className="bg-light text-center mb-0 pb-2 px-2 font-color-3">
							{contractVersionText}
						</Typography>
					</Card.CardContent>
				</Card>
				{isCompleteContract && <WorkspaceBox memberID={memberID} contractID={contractID} handleOpenDialog={handleOpenDialog} />}
				<MembershipRecordDialog open={isEqual(openTarget, dialogTypeMap.membershipRecord)} memberID={memberID} membershipID={membershipID} onClose={handleCloseDialog} />
				<MembershipBillingRecordDialog open={isEqual(openTarget, dialogTypeMap.membershipBillingRecord)} memberID={memberID} membershipID={membershipID} onClose={handleCloseDialog} />
			</FocusModeToggle>
		</MembershipProvider>
	);
};

export default MembershipCreateContent;
